'use client'

import { CircularProgress, TextField, TextFieldProps } from '@mui/material'
import MuiAutocomplete, {
    AutocompleteRenderGetTagProps,
    AutocompleteProps as MuiAutocompleteProps,
    autocompleteClasses,
} from '@mui/material/Autocomplete'
import Popper from '@mui/material/Popper'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { AutocompleteValue as Value } from '@mui/material/useAutocomplete'
import { FilterOptionsState } from '@mui/material/useAutocomplete/useAutocomplete'
import { omit } from 'lodash-es'
import {
    Fragment,
    HTMLAttributes,
    ReactElement,
    ReactNode,
    Ref,
    createContext,
    forwardRef,
    useContext,
    useMemo,
    useState,
} from 'react'
import { FixedSizeList, ListChildComponentProps } from 'react-window'
import { ExpandMoreIcon } from '../Icon/catalog'

const LISTBOX_PADDING = 8

type RowComponent = [HTMLAttributes<HTMLLIElement>, ReactElement]

function renderRow(props: ListChildComponentProps<RowComponent[]>) {
    const { data, index, style } = props
    const row = data[index]
    const outer = row && row.length > 0 ? row[0] : {}
    const inner = (row && row.length > 1 && row[1]) ?? null
    const outerProps = omit(outer, 'key')
    const wrapperStyle = {
        ...style,
        top: `${Number(style.top) + LISTBOX_PADDING}px`,
    }

    return (
        <Typography
            component="li"
            key={'key' in outer ? String(outer.key) : index}
            {...outerProps}
            noWrap
            style={wrapperStyle}
        >
            {inner}
        </Typography>
    )
}

const OuterElementContext = createContext({})

function OuterElementType({ ref, ...props }: { ref?: Ref<HTMLInputElement> }) {
    const outerProps = useContext(OuterElementContext)

    return <div ref={ref} {...props} {...outerProps} />
}

// Adapter for react-window
const ListboxComponent = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
    function ListboxComponent(props, ref) {
        const { children, ...other } = props
        const itemData: RowComponent[] = []

        ;(children as RowComponent[]).forEach((item: RowComponent & { children?: RowComponent[] }) => {
            itemData.push(item)
            itemData.push(...(item.children || []))
        })

        const itemSize = 48
        const itemCount = itemData.length
        const getHeight = () => {
            if (itemCount > 8) {
                return 8 * itemSize
            }

            return itemCount * itemSize
        }

        return (
            <div ref={ref}>
                <OuterElementContext.Provider value={other}>
                    <FixedSizeList
                        itemData={itemData}
                        height={getHeight() + 2 * LISTBOX_PADDING}
                        width="100%"
                        outerElementType={OuterElementType}
                        innerElementType="ul"
                        itemSize={itemSize}
                        overscanCount={5}
                        itemCount={itemCount}
                    >
                        {renderRow}
                    </FixedSizeList>
                </OuterElementContext.Provider>
            </div>
        )
    }
)

const StyledPopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
        boxSizing: 'border-box',
        '& ul': {
            padding: 0,
            margin: 0,
        },
    },
})

export type AutocompleteValue<
    T,
    Multiple extends boolean,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
> =
    Value<T, Multiple, DisableClearable, FreeSolo> extends Array<T>
        ? ReadonlyArray<T>
        : Value<T, Multiple, DisableClearable, FreeSolo>

export type AutocompleteProps<
    T,
    Multiple extends boolean,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
> = {
    id?: string
    label?: string
    multiple?: Multiple
    value?: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo> | null
    inputValue?: string
    options: ReadonlyArray<T>
    getOptionLabel: (option: T) => string
    renderOption: (option: T) => ReactNode
    renderTags?: (value: T[], getTagProps: AutocompleteRenderGetTagProps) => ReactNode
    onChange: (value: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>) => void
    filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[]
    renderInput?: MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['renderInput']
    disableBrowserSuggestions?: boolean
    error?: TextFieldProps['error']
    helperText?: ReactNode
    disableClearable?: DisableClearable
    onBlur?: TextFieldProps['onBlur']
    dataTestId?: string
    isFetchingNextPage?: boolean
    hideEmptyPopper?: boolean
} & Pick<
    MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
    | 'autoFocus'
    | 'className'
    | 'clearOnBlur'
    | 'clearOnEscape'
    | 'disabled'
    | 'filterSelectedOptions'
    | 'freeSolo'
    | 'forcePopupIcon'
    | 'fullWidth'
    | 'handleHomeEndKeys'
    | 'id'
    | 'isOptionEqualToValue'
    | 'limitTags'
    | 'loading'
    | 'noOptionsText'
    | 'onFocus'
    | 'onInputChange'
    | 'openOnFocus'
    | 'selectOnFocus'
    | 'size'
    | 'slotProps'
    | 'sx'
>

const LoadingText = () => (
    <Typography color="textSecondary" style={{ textAlign: 'center' }}>
        Loading...
    </Typography>
)
const popupIcon = <ExpandMoreIcon />
const getLimitTagsText = (more: number) => <Typography variant="body2">{`+${more}`}</Typography>

function Autocomplete<
    Args extends {
        valueType: unknown
        multiValued: boolean
        valueRequired: boolean
        freeInputAllowed: boolean
    },
>({
    id = '',
    label = '',
    value,
    options,
    renderOption,
    limitTags = 3,
    onChange,
    renderInput,
    disableBrowserSuggestions = false,
    error = false,
    helperText = '',
    loading = false,
    onBlur,
    dataTestId,
    isFetchingNextPage = false,
    autoFocus = false,
    hideEmptyPopper = false,
    ...rest
}: AutocompleteProps<
    Args['valueType'],
    Args['multiValued'],
    Args['valueRequired'],
    Args['freeInputAllowed']
>): ReactElement {
    const [isOpen, setIsOpen] = useState(autoFocus)
    const processValue = value as
        | Value<Args['valueType'], Args['multiValued'], Args['valueRequired'], Args['freeInputAllowed']>
        | undefined

    const popperComponent = useMemo(() => {
        if (!hideEmptyPopper) {
            return StyledPopper
        }
        return options.length > 0 || loading ? StyledPopper : () => null
    }, [hideEmptyPopper, loading, options.length])

    return (
        <MuiAutocomplete<Args['valueType'], Args['multiValued'], Args['valueRequired'], Args['freeInputAllowed']>
            id={id}
            options={[...options, ...(isFetchingNextPage ? [true] : [])]}
            PopperComponent={popperComponent}
            ListboxComponent={ListboxComponent}
            value={processValue}
            renderOption={(props, option, state) => {
                const isEndOfList = state.index === options.length

                if (isEndOfList) {
                    return [{}, <LoadingText key="loading" />] as unknown as ReactElement
                }

                return [props, renderOption(option)] as unknown as ReactElement
            }}
            limitTags={limitTags}
            getLimitTagsText={getLimitTagsText}
            onChange={(_, value) =>
                onChange(
                    value as AutocompleteValue<
                        Args['valueType'],
                        Args['multiValued'],
                        Args['valueRequired'],
                        Args['freeInputAllowed']
                    >
                )
            }
            renderInput={
                renderInput
                    ? renderInput
                    : (params) => (
                          <TextField
                              {...params}
                              label={label}
                              autoComplete={disableBrowserSuggestions ? 'off' : ''}
                              inputProps={{
                                  ...params.inputProps,
                                  autoFocus,
                                  autoComplete: disableBrowserSuggestions ? 'chrome-off' : '',
                              }}
                              InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                      <Fragment>
                                          {isOpen && (loading || isFetchingNextPage) ? (
                                              <CircularProgress color="inherit" size={20} />
                                          ) : null}
                                          {params.InputProps.endAdornment}
                                      </Fragment>
                                  ),
                              }}
                              error={error}
                              helperText={helperText}
                              onBlur={onBlur}
                          />
                      )
            }
            loading={loading}
            autoFocus={autoFocus}
            popupIcon={popupIcon}
            data-testid={dataTestId}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            {...rest}
        />
    )
}

export default Autocomplete
